// Package Imports
import { useQuery } from '@tanstack/react-query'

// Module Imports
import { authUserQueryOptions } from '@/modules/auth/auth.hooks'
import type { TPermissionKey } from '@/modules/role/role.types'

/**
 * Read the currently authenticated user from the query cache.
 *
 * `useQuery` is intentional — `useSuspenseQuery` throws on failure, forcing
 * callers to either wrap in an error boundary or catch the throw (which
 * violates rules-of-hooks). Returning `null` keeps consumers safe.
 */
export const useAuth = () => {
  const { data: user, isPending, isError } = useQuery(authUserQueryOptions())

  const hasPermission = (permission: TPermissionKey): boolean =>
    user?.permissions?.includes(permission) ?? false

  return {
    user: user ?? null,
    isAuthenticated: !!user,
    isLoading: isPending,
    isError,
    hasPermission,
  }
}
